/**
 * Format a packing operation for display as "source1, source2 → dest1, dest2"
 * using event and storage location names (never raw IDs).
 * Also provides operation status helpers and progress calculation.
 */

/** Minimal operation shape for status and progress (e.g. PackingOperation or trip.packingOperations[n]). */
export interface OperationListItemLike {
    status?: string | null;
    completedAt?: number | string | null;
    packingTasks?: Array<{ completed?: boolean }> | null;
}

export function isOperationActive(op: OperationListItemLike): boolean {
    return op.status !== 'completed' && op.completedAt == null;
}

export function isOperationCompleted(op: OperationListItemLike): boolean {
    return op.status === 'completed' || op.completedAt != null;
}

export function splitOperationsActiveCompleted<T extends OperationListItemLike>(ops: T[]): { active: T[]; completed: T[] } {
    const active: T[] = [];
    const completed: T[] = [];
    for (const op of ops) {
        if (isOperationActive(op)) active.push(op);
        else completed.push(op);
    }
    return { active, completed };
}

export function getOperationProgress(op: OperationListItemLike): {
    completed: number;
    total: number;
    percentage: number;
} {
    const tasks = op.packingTasks ?? [];
    const total = tasks.length;
    const completed = tasks.filter((t: { completed?: boolean }) => t.completed).length;
    return { completed, total, percentage: total > 0 ? (completed / total) * 100 : 0 };
}

function name(x: { name?: string | null } | null | undefined): string {
    const n = x?.name?.trim();
    return n ?? 'Unnamed';
}

/** Minimal operation-like shape (e.g. from trip.packingOperations or full PackingOperation). */
export interface OperationSummaryInput {
    sourceEvents?: Array<{ name?: string | null }> | null;
    targetEvents?: Array<{ name?: string | null }> | null;
    sourceStorageLocations?: Array<{ name?: string | null }> | null;
    targetStorageLocations?: Array<{ name?: string | null }> | null;
}

export function formatOperationSummary(operation: OperationSummaryInput): string {
    const sourceStorage = (operation.sourceStorageLocations ?? []).map(name);
    const sourceEvents = (operation.sourceEvents ?? []).map(name);
    const targetEvents = (operation.targetEvents ?? []).map(name);
    const targetStorage = (operation.targetStorageLocations ?? []).map(name);

    const sources = [...sourceStorage, ...sourceEvents].filter(Boolean);
    const targets = [...targetEvents, ...targetStorage].filter(Boolean);

    if (sources.length === 0 && targets.length === 0) return 'Operation';
    if (sources.length === 0) return `→ ${targets.join(', ')}`;
    if (targets.length === 0) return `${sources.join(', ')} →`;
    return `${sources.join(', ')} → ${targets.join(', ')}`;
}
